/* eslint-disable react/jsx-indent */
import {
  Badge,
  Box,
  Flex,
  Group,
  MantineColor,
  MantineStyleProp,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Currency } from '@models/ropolo';
import currencyUtil from '@utils/currencyUtil';

declare type ICurrency = Currency.RIAL | Currency.TOMAN;

export const FormattedPrice = ({
  price,
  color,
  showZero = false,
  priceTextSize = 'md',
  currency = Currency.TOMAN,
  hideCurrency = false,
  style,
  compact = false,
}: {
  price: number;
  color?: MantineColor;
  showZero?: boolean;
  priceTextSize?: string;
  currency?: ICurrency;
  hideCurrency?: boolean;
  style?: MantineStyleProp;
  compact?: boolean;
}) => {
  const isFree = price === 0;

  const formattedPrice = currencyUtil.formatPrice({ price, currency, showZero });

  const label = currencyUtil.mapCurrencyLabel(currency, compact);

  return (
    <Flex style={style} gap="0.3rem" align="center">
      <Text data-cy="TextPrice" size={priceTextSize} color={color}>
        {formattedPrice}
      </Text>

      {isFree
        ? null
        : !hideCurrency && (
            <Text size="xs" color={color} fw={300}>
              {label}
            </Text>
          )}
    </Flex>
  );
};

const Price = ({
  price,
  discount,
  priceTextSize = 'md',
  variant = 'site',
  currency = Currency.TOMAN,
}: {
  price: number;
  discount?: number;
  priceTextSize?: string;
  currency?: ICurrency;
  variant?: 'site' | 'bo';
}) => {
  const discountPercentage = discount ? (100 * discount) / price : 0;
  const finalAmount = price - (discount || 0);

  const hasDiscount = finalAmount !== price;

  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const colorInLight = hasDiscount ? theme.colors.brand[3] : theme.colors.brand[8];
  const colorInDark = hasDiscount ? theme.colors.brand[2] : theme.colors.brand[1];
  const color: MantineColor = colorScheme === 'dark' ? colorInDark : colorInLight;
  return (
    <Box>
      <Stack gap="xs">
        <Group gap="xs" align="center">
          {discountPercentage > 0 && (
            <Badge radius="md" leftSection="%" variant="filled">
              {Math.ceil(discountPercentage)}
            </Badge>
          )}
          <Box style={{ textDecoration: hasDiscount && variant === 'bo' ? 'line-through' : undefined }}>
            <FormattedPrice priceTextSize={priceTextSize} price={price} currency={currency} color={color} />
          </Box>
        </Group>

        {hasDiscount && (
          <Group gap="xs" align="center">
            <FormattedPrice priceTextSize={priceTextSize} price={finalAmount} currency={currency} />
          </Group>
        )}
      </Stack>
    </Box>
  );
};

export default Price;
